import React, { forwardRef } from 'react';

import TableM, { TableProps as TablePropsM } from '@mui/material/Table';

import { TableContext } from './TableContext.js';

type SkeletonVariants = undefined | 'default' | 'checkbox' | 'avatar';

export interface TableProps extends TablePropsM {
  /**
   * The content of the component, normally TableRow.

   * */
  children?: React.ReactNode;

  /**
   * Override or extend the styles applied to the component.
   * See CSS API below for more details.

   */
  classes?: TablePropsM['classes'];

  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   */
  component?: React.ElementType;

  /**
   * Allows TableCells to inherit padding of the Table.
   */
  padding?: TablePropsM['padding'];
  /**
   * Allows TableCells to inherit size of the Table.
   */
  size?: TablePropsM['size'];
  /**
   * Set the header sticky.
   * ⚠️ It doesn't work with IE11.
   */
  stickyHeader?: boolean;
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   * See the `sx` page for more details.
   */
  sx?: TablePropsM['sx'];
  isLoading?: boolean;
  placeholderRowsCount?: number;
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ isLoading = false, placeholderRowsCount = 10, ...props }, ref) => {
    const [variants, setVariants] = React.useState<SkeletonVariants[]>([]);

    const className = [props.className, isLoading && 'isLoading']
      .filter(Boolean)
      .join(' ');

    return (
      <TableContext.Provider
        value={{
          variants: variants,
          setVariants,
          isLoading,
          placeholderRowsCount,
        }}
      >
        <TableM ref={ref} {...props} className={className}>
          {props.children}
        </TableM>
      </TableContext.Provider>
    );
  },
);

export default Table;
